/**
 * BoatTelemetry.ts — pure derivation of HUD/telemetry numbers from a
 * boat's instantaneous state plus the ambient wind.
 *
 * Lifted out of `main.ts` so the maths (apparent-wind decomposition,
 * COG/SOG, compass-bearing normalisation) can be unit-tested in
 * isolation without spinning up GPU + render passes. The orchestrator
 * holds onto a closure (`buildBoatTelemetry`) that captures the boat
 * + state references; calling it returns a fresh snapshot every time.
 *
 * Conventions (same as the rest of the sim):
 *   • Body axes: +X forward (bow), +Y up, +Z starboard.
 *   • Heading angle θ stored in `boat.heading`; forward world vector
 *     is (sin θ, -cos θ) so 0 = north (-Z), +π/2 = east (+X).
 *   • Wind direction in `state.windDirectionDeg` is meteorological
 *     azimuth (where the wind comes FROM). The world wind VECTOR
 *     points in the opposite direction.
 *   • All speeds m/s, all angles radians on output. AWA in (-π, π],
 *     positive = wind from starboard.
 */
import type { Boat } from "./Boat";
import { RUDDER_MAX_ANGLE_RAD } from "./Boat";
import { DEFAULT_WHEEL_GEAR_RATIO, type SteeringControlKind } from "./BoatHull";

export interface BoatTelemetry {
  throttle: number;
  /** Engine gas grip in [0, 1] — independent from the gear lever. */
  gas: number;
  /** Gear lever: -1 reverse, 0 neutral, +1 forward. */
  gear: -1 | 0 | 1;
  steer: number;
  /** Heading (bow direction), compass bearing radians. */
  heading: number;
  /** Course over ground, compass bearing radians. */
  cog: number;
  /** Speed over ground, m/s. */
  sog: number;
  surge: number;
  sway: number;
  /** True wind direction (where the wind vector points TO), radians. */
  twd: number;
  /** True wind speed, m/s. */
  tws: number;
  /** Apparent wind angle relative to bow, radians. */
  awa: number;
  /** Apparent wind speed, m/s. */
  aws: number;
  rudderAngle: number;
  /** Actual outboard angle, radians. Equals rudderAngle when coupled. */
  engineAngle: number;
  /** Whether an outboard is linked to the helm/rudder. */
  engineSteeringCoupled: boolean;
  /** Wheel rotation from centre to full lock, degrees, using the active hull ratio. */
  helmWheelLockDeg: number;
  posX: number;
  posZ: number;
  /** Engine kind for the player boat (drives engine sound selection). */
  engineType: "inboard" | "outboard";
  /** Cockpit helm installed on the active boat. */
  steering: SteeringControlKind;
}

/** Minimal state slice the telemetry calculation needs. */
export interface TelemetryWindState {
  windDirectionDeg: number;
  windSpeed: number;
}

const TAU = Math.PI * 2;

/** Normalise an angle to [0, 2π). */
function normalizeBearing(a: number): number {
  let r = a % TAU;
  if (r < 0) r += TAU;
  return r;
}

/**
 * Compute a fresh telemetry snapshot from the live boat + wind state.
 * Pure function — does not retain any references between calls.
 */
export function computeBoatTelemetry(
  boat: Boat,
  wind: TelemetryWindState,
): BoatTelemetry {
  const windRad = (wind.windDirectionDeg * Math.PI) / 180;
  // World wind VECTOR (where it blows TO). UI wind dir is meteorological
  // (where it comes FROM); blowing TO = the opposite direction. Using
  // (-sin, +cos) matches the world frame convention everywhere else.
  const windX = -Math.sin(windRad) * wind.windSpeed;
  const windZ = Math.cos(windRad) * wind.windSpeed;

  // Boat body axes in world frame.
  const ch = Math.cos(boat.heading);
  const sh = Math.sin(boat.heading);
  const fwdX = sh;
  const fwdZ = -ch;
  const rgtX = ch;
  const rgtZ = sh;

  // Boat-frame velocity → world-frame.
  const vx = boat.surge * fwdX + boat.sway * rgtX;
  const vz = boat.surge * fwdZ + boat.sway * rgtZ;
  const sog = Math.hypot(vx, vz);

  // Apparent wind = true wind − boat velocity.
  const appX = windX - vx;
  const appZ = windZ - vz;
  const aws = Math.hypot(appX, appZ);
  // AWA in body frame: x = forward, z = starboard.
  const appBodyX = appX * fwdX + appZ * fwdZ;
  const appBodyZ = appX * rgtX + appZ * rgtZ;
  const awa = Math.atan2(appBodyZ, appBodyX);

  const heading = normalizeBearing(Math.atan2(fwdX, -fwdZ));
  const cog = sog > 1e-3 ? normalizeBearing(Math.atan2(vx, -vz)) : heading;
  const twd = normalizeBearing(Math.atan2(windX, -windZ));

  return {
    throttle: boat.throttle,
    gas: boat.gas,
    gear: boat.gear,
    steer: boat.steer,
    heading,
    cog,
    sog,
    surge: boat.surge,
    sway: boat.sway,
    twd,
    tws: wind.windSpeed,
    awa,
    aws,
    rudderAngle: boat.rudderAngle,
    engineAngle: boat.engineAngle,
    helmWheelLockDeg:
      (RUDDER_MAX_ANGLE_RAD *
        (boat.params.wheelGearRatio ?? DEFAULT_WHEEL_GEAR_RATIO) *
        180) /
      Math.PI,
    posX: boat.posX,
    posZ: boat.posZ,
    engineType: boat.params.engineType ?? "inboard",
    engineSteeringCoupled: boat.params.engineSteeringCoupled,
    steering: boat.params.steering,
  };
}
